import { FormattedDate, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ContentBanner } from '@xing-com/banner';
import type { InteractionTrackingEvent } from '@xing-com/crate-communication-tracking';
import { useTracking } from '@xing-com/crate-communication-tracking';
import type { ApplicationData } from '@xing-com/crate-jobs-helpers';
import type { PropTrackActionData } from '@xing-com/crate-jobs-hooks';
import { useJobsNwtTracking } from '@xing-com/crate-jobs-tracking';
import { Hyperlink } from '@xing-com/hyperlink';
import { IconEvents, IconTick } from '@xing-com/icons';
import { mediaConfined } from '@xing-com/layout-tokens';
import { spaceL, spaceM, spaceS, spaceXXL } from '@xing-com/tokens';
import { BodyCopy } from '@xing-com/typography';

import { useApplicationStatusHelpers, useInterviewBooking } from '../../hooks';

const Banner = styled(ContentBanner)`
  padding: ${spaceM};
  justify-content: center;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${spaceS} ${spaceXXL};

  @media ${mediaConfined} {
    padding: ${spaceL};
    margin-bottom: ${spaceL};
  }
`;

const ConfirmationItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${spaceM};
`;

type AdobeTrackingProps = Required<PropTrackActionData> & {
  PropJobsId: number | string;
};

type Props = {
  application: ApplicationData;
  className?: string;
};

export const Confirmation: React.FC<Props> = ({
  application,
  className, // this is important to receive styles from outside (see header.tsx)
}) => {
  const { appliedAt } = useApplicationStatusHelpers(application);
  const { slug, jobId } = application;
  const { isLoading, hasBookedInterview, editInterviewUrl } =
    useInterviewBooking({ slug, jobId });
  const nwtTrack = useJobsNwtTracking();
  const { track: trackAdobeEvent } =
    useTracking<InteractionTrackingEvent<AdobeTrackingProps>>();

  const hasApplyConfirmation = !!appliedAt;
  const hasBookedInterviewConfirmation = !isLoading && hasBookedInterview;

  const onDetailsClick = (): void => {
    trackAdobeEvent({
      type: 'interaction',
      event: 'PropTrackAction',
      PropTrackAction: 'jobs_interview_apply_details',
      PropActionOrigin: 'job_interview_instant_apply',
      PropJobsId: jobId,
    });
    nwtTrack({
      eventSchema: 'basic',
      itemUrn: `surn:x-xing:jobs:posting:${jobId}`,
      page: 'jobs/detail',
      event: 'opened',
      element: 'interview_apply_details',
      context: 'interview_instant_apply',
    });
  };

  if (!hasApplyConfirmation && !hasBookedInterviewConfirmation) return null;

  return (
    <Banner variant="default" display="inline" className={className} noPadding>
      {hasApplyConfirmation ? (
        <ConfirmationItem>
          <IconTick width={18} height={18} />
          <BodyCopy noMargin>
            <FormattedMessage
              id="JOBS_JOB_DETAILS_PREVIOUSLY_APPLIED_CONFIRMATION"
              values={{
                date: (
                  <FormattedDate
                    value={appliedAt}
                    year="numeric"
                    month="short"
                    day="2-digit"
                  />
                ),
              }}
            />
          </BodyCopy>
        </ConfirmationItem>
      ) : null}
      {hasBookedInterviewConfirmation ? (
        <ConfirmationItem>
          <IconEvents width={18} height={18} />
          <BodyCopy noMargin>
            <FormattedMessage
              id="JOBS_JOB_DETAILS_INTERVIEW_BOOKED_CONFIRMATION"
              values={{
                link: (chunks) => (
                  <Hyperlink
                    href={editInterviewUrl}
                    external
                    // @ts-expect-error FIXME: SC6
                    rel="noopener"
                    onClick={onDetailsClick}
                  >
                    {chunks}
                  </Hyperlink>
                ),
              }}
            />
          </BodyCopy>
        </ConfirmationItem>
      ) : null}
    </Banner>
  );
};
